import { apiFetch } from '@/lib/api/client';
import type {
  ActivityView,
  CoverageView,
  CreateCorrectionInput,
  CreatePersonalTargetInput,
  CreateSubmissionInput,
  CreateTargetInput,
  EvaluateSubmissionInput,
  EvaluationOutcome,
  EvaluationResult,
  ProgressSummaryView,
  SegmentView,
  StudentProgressView,
  SubmissionStatus,
  SubmissionView,
  TargetView,
  UpdateTargetInput,
} from '@/features/memorization/types';
import type { PagedData } from '@/lib/api/types';

/** `GET /memorization/targets`. Tenant-scoped (ADR-026 Addendum, AO-2). Narrowed to Self/Child server-side. */
export async function listTargets(
  studentId: string,
  page = 1,
  limit = 20,
): Promise<PagedData<TargetView>> {
  const query = new URLSearchParams({
    studentId,
    page: String(page),
    limit: String(limit),
  });
  return apiFetch<PagedData<TargetView>>(`/memorization/targets?${query.toString()}`, {
    tenantScoped: true,
  });
}

/** `GET /memorization/targets/:id`. Tenant-scoped. */
export async function getTarget(id: string): Promise<TargetView> {
  return apiFetch<TargetView>(`/memorization/targets/${id}`, { tenantScoped: true });
}

/** `GET /memorization/targets/:id/coverage`. Tenant-scoped. The LIVE status/coverage read. */
export async function getTargetCoverage(id: string): Promise<CoverageView> {
  return apiFetch<CoverageView>(`/memorization/targets/${id}/coverage`, { tenantScoped: true });
}

/** `POST /memorization/targets`. Tenant-scoped; requires `memorization_targets:manage`. */
export async function createTarget(input: CreateTargetInput): Promise<TargetView> {
  return apiFetch<TargetView>('/memorization/targets', {
    method: 'POST',
    body: input,
    tenantScoped: true,
  });
}

/**
 * `POST /memorization/personal-targets` (ADR-031, Decision 6). A solo
 * learner setting a target for themselves. Deliberately carries NO
 * `studentId`: the server derives it from the authenticated caller and
 * refuses outside the caller's own PERSONAL space, so this call is
 * structurally incapable of targeting anyone else.
 */
export async function createPersonalTarget(input: CreatePersonalTargetInput): Promise<TargetView> {
  return apiFetch<TargetView>('/memorization/personal-targets', {
    method: 'POST',
    body: input,
    tenantScoped: true,
  });
}

/** `PATCH /memorization/targets/:id`. Tenant-scoped; requires `memorization_targets:manage`. */
export async function updateTarget(id: string, input: UpdateTargetInput): Promise<TargetView> {
  return apiFetch<TargetView>(`/memorization/targets/${id}`, {
    method: 'PATCH',
    body: input,
    tenantScoped: true,
  });
}

/**
 * `DELETE /memorization/targets/:id` (`cancelTargetHandler`). Tenant-scoped;
 * requires `memorization_targets:manage`. Soft cancellation - there is no
 * un-cancel endpoint.
 */
export async function cancelTarget(id: string): Promise<void> {
  await apiFetch<unknown>(`/memorization/targets/${id}`, {
    method: 'DELETE',
    tenantScoped: true,
  });
}

/**
 * `GET /memorization/progress/:studentId`. Tenant-scoped; narrowed to
 * Self/Child at the object level (ADR-019's Addendum, Decision 17) - the
 * same permission `memorization_targets:read` this module's other reads use.
 */
export async function getStudentProgress(studentId: string): Promise<StudentProgressView> {
  return apiFetch<StudentProgressView>(`/memorization/progress/${studentId}`, {
    tenantScoped: true,
  });
}

/**
 * Cross-student aggregate Progress reads (Phase G-6; ADR-019's
 * Addendum-to-the-Addendum, 2026-08-12): `memorization_targets:manage`,
 * not `:read` - a Teacher/Admin-only surface in v1, since a class/
 * learning-space/organization summary has no per-student row for
 * Self/Child scoping to narrow. Backed by a materialized summary row
 * (`lastReconciledAt` is meaningful here, unlike the individual read).
 */
export async function getClassProgress(classId: string): Promise<ProgressSummaryView> {
  return apiFetch<ProgressSummaryView>(`/memorization/progress/classes/${classId}`, {
    tenantScoped: true,
  });
}

export async function getLearningSpaceProgress(
  learningSpaceId: string,
): Promise<ProgressSummaryView> {
  return apiFetch<ProgressSummaryView>(
    `/memorization/progress/learning-spaces/${learningSpaceId}`,
    { tenantScoped: true },
  );
}

export async function getOrganizationProgress(
  organizationId: string,
): Promise<ProgressSummaryView> {
  return apiFetch<ProgressSummaryView>(`/memorization/progress/organizations/${organizationId}`, {
    tenantScoped: true,
  });
}

/**
 * `POST .../reconcile` (ADR-024 Decision 5). Requires `settings:manage` -
 * Organization Admin/Super Admin only (Teacher does not hold this
 * permission, per `permissions.ts`). Recomputes every materialized
 * summary row for the organization from source data.
 */
export async function reconcileOrganizationProgress(organizationId: string): Promise<void> {
  await apiFetch<unknown>(`/memorization/progress/organizations/${organizationId}/reconcile`, {
    method: 'POST',
    tenantScoped: true,
  });
}

/**
 * `GET /memorization/setoran`. Tenant-scoped. Narrowed to Self/Child
 * server-side (`memorization_targets:manage` signals broad access -
 * fixed in this phase, see the G-2 contract-audit finding).
 *
 * `studentId` is genuinely optional in the backend's own
 * `listSubmissionsQuerySchema` - omitting it lists every submission the
 * caller's resolved read scope allows (Phase G-3: a Teacher/Admin's broad
 * scope makes this an org-wide list, used for the "awaiting evaluation"
 * workspace surface; every existing G-2 call site keeps passing a
 * studentId and is unaffected).
 */
export async function listSubmissions(
  studentId: string | undefined,
  page = 1,
  limit = 20,
  filter: { readonly targetId?: string; readonly status?: SubmissionStatus } = {},
): Promise<PagedData<SubmissionView>> {
  const query = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (studentId !== undefined) {
    query.set('studentId', studentId);
  }
  if (filter.targetId !== undefined) {
    query.set('targetId', filter.targetId);
  }
  if (filter.status !== undefined) {
    query.set('status', filter.status);
  }
  return apiFetch<PagedData<SubmissionView>>(`/memorization/setoran?${query.toString()}`, {
    tenantScoped: true,
  });
}

/** `GET /memorization/setoran/:id`. Tenant-scoped. */
export async function getSubmission(id: string): Promise<SubmissionView> {
  return apiFetch<SubmissionView>(`/memorization/setoran/${id}`, { tenantScoped: true });
}

/** `POST /memorization/setoran`. Tenant-scoped; requires `memorization_records:manage`. */
export async function createSubmission(input: CreateSubmissionInput): Promise<SubmissionView> {
  return apiFetch<SubmissionView>('/memorization/setoran', {
    method: 'POST',
    body: input,
    tenantScoped: true,
  });
}

/**
 * `POST /memorization/setoran/:id/evaluations`. Tenant-scoped; requires
 * `evaluations:manage`. Fails with a 422 (surfaced via `ApiError.message`)
 * if the submission was already evaluated, or if the segments do not
 * exactly tile the submission's own range.
 */
export async function evaluateSubmission(
  submissionId: string,
  input: EvaluateSubmissionInput,
): Promise<EvaluationResult> {
  return apiFetch<EvaluationResult>(`/memorization/setoran/${submissionId}/evaluations`, {
    method: 'POST',
    body: input,
    tenantScoped: true,
  });
}

/**
 * `POST /memorization/targets/:id/corrections`. Tenant-scoped; requires
 * `evaluations:manage`. A standalone segment, always NEEDS_REVISION
 * server-side - `outcome` is never part of the request.
 */
export async function createCorrection(
  targetId: string,
  input: CreateCorrectionInput,
): Promise<EvaluationResult> {
  return apiFetch<EvaluationResult>(`/memorization/targets/${targetId}/corrections`, {
    method: 'POST',
    body: input,
    tenantScoped: true,
  });
}

/**
 * `GET /memorization/targets/:id/evaluations`. Tenant-scoped. Narrowed to
 * Self/Child; each segment individually redacted per LIMITED_READ
 * (ADR-025's Addendum) - `scoreAccuracy`/`scoreFluency`/`scoreTajwid`/
 * `scoreConfidence`/`notes` may already be `null` in the response for a
 * LIMITED_READ Guardian caller.
 */
export async function listEvaluations(
  targetId: string,
  page = 1,
  limit = 20,
  filter: { readonly submissionId?: string; readonly outcome?: EvaluationOutcome } = {},
): Promise<PagedData<SegmentView>> {
  const query = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (filter.submissionId !== undefined) {
    query.set('submissionId', filter.submissionId);
  }
  if (filter.outcome !== undefined) {
    query.set('outcome', filter.outcome);
  }
  return apiFetch<PagedData<SegmentView>>(
    `/memorization/targets/${targetId}/evaluations?${query.toString()}`,
    { tenantScoped: true },
  );
}

/**
 * `GET /memorization/activity/:studentId` (ADR-034). Sends the browser's
 * own timezone offset so the server buckets setoran into the days the
 * reader actually experienced - a WIB santri submitting at 06:00 is at
 * 23:00 the previous day in UTC, and bucketing by UTC would break their
 * streak at breakfast.
 */
export async function getStudentActivity(
  studentId: string,
  days = 30,
): Promise<ActivityView> {
  const tzOffsetMinutes = new Date().getTimezoneOffset();
  const params = new URLSearchParams({
    days: String(days),
    tzOffsetMinutes: String(tzOffsetMinutes),
  });
  return apiFetch<ActivityView>(`/memorization/activity/${studentId}?${params.toString()}`, {
    tenantScoped: true,
  });
}
